import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import ContactForm from "./components/ContactForm";
import { brandValues, milestones, testimonials } from "@/mocks/about";

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      name: "关于我们 - 蝌蚪黑料",
      url: "https://bhmbk.cn/about",
    },
    {
      "@type": "Organization",
      name: "蝌蚪黑料",
      url: "https://bhmbk.cn/",
      email: "hello@kedou-heiliao.example.com",
      description: "内容整理与话题追踪平台，将热门事件按话题整理成带时间线的合集。",
    },
  ],
};

export default function About() {
  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <Navbar />
      <main className="flex-1 pt-16 md:pt-[72px]">
        <section className="relative w-full overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=abstract%20warm%20crimson%20and%20amber%20layered%20paper%20texture%20with%20soft%20light%2C%20minimal%20editorial%20background%2C%20no%20people%2C%20no%20text&width=1800&height=800&seq=about-hero-01&orientation=landscape"
              alt="蝌蚪黑料品牌介绍背景"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/70 to-foreground-950/40"></div>
          </div>
          <div className="relative z-10 max-w-content mx-auto w-full px-4 md:px-8 py-14 md:py-20">
            <div className="[&_a]:text-background-200 [&_span]:text-background-100">
              <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            </div>
            <h1 className="mt-5 font-heading text-3xl md:text-5xl font-black text-background-50 max-w-3xl leading-tight">
              做整理，不做定性
            </h1>
            <p className="mt-5 max-w-2xl text-sm md:text-base text-background-100 leading-relaxed">
              蝌蚪黑料是一个把热门事件按话题整理成合集的内容平台。我们不追逐情绪，只把事情按时间顺序摆清楚，
              让想了解来龙去脉的人，能在最短时间里看懂。
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="max-w-content mx-auto w-full px-4 md:px-8 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
              <div className="lg:col-span-5">
                <span className="text-xs md:text-sm font-medium text-primary-600 tracking-wide">
                  POSITIONING / 网站定位
                </span>
                <h2 className="mt-2 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                  一个话题，一条时间线
                </h2>
              </div>
              <div className="lg:col-span-7 space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  我们注意到，大部分热点信息是碎片化的：同一条消息被反复转发，不同时间的内容混在一起，
                  想搞清楚「现在到底进展到哪一步」，往往要翻很久。
                </p>
                <p>
                  于是我们选择以「合集」为单位来组织内容。每个合集只讲一个话题，用时间线固定先后顺序，
                  把起因、关键进展和后续更新分层呈现，让你既能快速浏览精华，也能按日期逐条细看。
                </p>
                <p>
                  我们不提供结论，也不替任何人下判断。内容以公开渠道可查证的信息为主，
                  涉及具体争议时，一律以当事人、平台或官方渠道的正式说明为准。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="principles" className="w-full bg-background-100 border-y border-background-200 scroll-mt-20">
          <div className="max-w-content mx-auto w-full px-4 md:px-8 py-14 md:py-20">
            <div className="mb-8 md:mb-10">
              <span className="text-xs md:text-sm font-medium text-primary-600 tracking-wide">
                PRINCIPLES / 内容原则
              </span>
              <h2 className="mt-2 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                我们坚持的几件事
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {brandValues.map((v) => (
                <div
                  key={v.title}
                  className="p-5 rounded-lg bg-background-50 border border-background-200"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                    <i className={`${v.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{v.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="milestones" className="w-full bg-background-50 scroll-mt-20">
          <div className="max-w-content mx-auto w-full px-4 md:px-8 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
              <div className="lg:col-span-4">
                <span className="text-xs md:text-sm font-medium text-primary-600 tracking-wide">
                  MILESTONES / 发展历程
                </span>
                <h2 className="mt-2 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                  一步一个脚印
                </h2>
                <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                  从一个小型话题整理项目，慢慢长成一个稳定更新的内容平台。
                </p>
              </div>
              <div className="lg:col-span-8">
                <div className="relative pl-6 md:pl-8">
                  <div className="absolute left-[7px] md:left-[9px] top-2 bottom-2 w-px bg-background-300"></div>
                  <ul className="space-y-7">
                    {milestones.map((m) => (
                      <li key={m.year} className="relative">
                        <span className="absolute -left-6 md:-left-8 top-1.5 w-3.5 h-3.5 rounded-full bg-primary-500 border-2 border-background-50"></span>
                        <span className="font-heading text-xl font-bold text-foreground-900">{m.year}</span>
                        <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{m.text}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 border-y border-background-200">
          <div className="max-w-content mx-auto w-full px-4 md:px-8 py-14 md:py-20">
            <div className="mb-8 md:mb-10">
              <span className="text-xs md:text-sm font-medium text-primary-600 tracking-wide">
                VOICES / 用户口碑
              </span>
              <h2 className="mt-2 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                读者怎么说
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t) => (
                <figure key={t.name} className="p-6 rounded-lg bg-background-50 border border-background-200">
                  <i className="ri-double-quotes-l text-2xl text-accent-500"></i>
                  <blockquote className="mt-3 text-sm text-foreground-700 leading-relaxed">
                    {t.text}
                  </blockquote>
                  <figcaption className="mt-5 pt-4 border-t border-background-200 flex items-center gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-500 text-background-50 text-sm">
                      {t.name.slice(0, 1)}
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-900">{t.name}</span>
                      <span className="block text-xs text-foreground-500">{t.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="w-full bg-background-50 scroll-mt-20">
          <div className="max-w-content mx-auto w-full px-4 md:px-8 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
              <div className="lg:col-span-5">
                <span className="text-xs md:text-sm font-medium text-primary-600 tracking-wide">
                  CONTACT / 联系我们
                </span>
                <h2 className="mt-2 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                  有反馈，随时说
                </h2>
                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  发现合集里的信息有出入，或者希望我们收录某个话题，都可以通过表单告诉我们。我们会在核对后更新，并尽量回复每一条反馈。
                </p>
                <div className="mt-7 space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 border border-background-200 text-primary-600">
                      <i className="ri-mail-line"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">反馈邮箱</p>
                      <a
                        href="mailto:hello@kedou-heiliao.example.com"
                        className="text-sm text-foreground-900 hover:text-primary-600 transition-colors"
                      >
                        hello@kedou-heiliao.example.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 border border-background-200 text-primary-600">
                      <i className="ri-time-line"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">回复时间</p>
                      <p className="text-sm text-foreground-900">工作日 1–2 天内</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 border border-background-200 text-primary-600">
                      <i className="ri-compasses-2-line"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">还不熟悉？</p>
                      <Link to="/faq" className="text-sm text-foreground-900 hover:text-primary-600 transition-colors">
                        先看看常见问题
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-7">
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}